import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ReviewProgress } from '../../lib/api.js';
import { ContinueWithOpenBreakdownDialog } from '../../components/ContinueWithOpenBreakdownDialog.js';
import { ReviewRequirements } from '../tabs/ReviewRequirements.js';

/**
 * Why Continue is not available AT ALL — only when there is nothing to go on
 * with. Everything short of a complete review is a note beside an enabled
 * button instead (`continueNote`).
 */
export function continueBlockedReasonFor(p: ReviewProgress | undefined): string | null {
  if (p === undefined || p.reviewComplete) return null;
  if (p.sceneCount === 0) return 'No scenes yet';
  if (p.total === 0) return 'No location requirements yet — analyse the screenplay or add one';

  return null;
}

/** What is still open, said beside the enabled Continue. */
export function continueNoteFor(p: ReviewProgress | undefined): string | null {
  if (p === undefined || p.reviewComplete || continueBlockedReasonFor(p) !== null) return null;

  const parts = [
    p.proposed > 0
      ? `${String(p.proposed)} requirement${p.proposed === 1 ? '' : 's'} still need${p.proposed === 1 ? 's' : ''} a decision`
      : null,
    p.scenesWithoutRequirements > 0
      ? `${String(p.scenesWithoutRequirements)} scene${p.scenesWithoutRequirements === 1 ? ' has' : 's have'} no requirement yet`
      : null,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? null : `${parts.join(' · ')} — you can go on and finish them later`;
}

/**
 * THE LOCATIONS WORKFLOW, page 1 — the requirements review, in the guided shell.
 * (It was "step 3" of a linear path until 2026-10-29; Locations and the
 * detailed breakdown are now independent workflows after the confirmed scenes.)
 *
 * `ReviewRequirementsScreen` already IS the design's breakdown review: scene
 * rail, script excerpt with marks, production elements, confidence and reason,
 * manual corrections, merge and split. The decision itself — "we need this
 * location" / "we don't need this" — is the Sourcing list's approve and
 * dismiss, now on the scene card as well, against the same requirement row.
 *
 * CONTINUE NO LONGER WAITS FOR `reviewComplete` (owner review 2026-10-27,
 * D1). It is disabled only when there is nothing to go on with — no scene, no
 * requirement. Short of a complete review it asks first: the dialog names the
 * requirements still undecided and the scenes with none, says they will not
 * be searched for, and "Continue anyway" records the choice
 * (`breakdown-continued`), which is what lets the route take the producer on
 * to Explore. A complete review goes straight on, as before.
 */
export function BreakdownStep({
  projectId,
  projectTitle,
  focusSceneId = null,
  onSceneChange,
  onContinueToMatching,
}: {
  projectId: string;
  projectTitle: string;
  /** `?scene=` — the scene to open on (step 2's Review column links here with it). */
  focusSceneId?: string | null;
  /** Told when the scene on screen changes, so the route can keep `?scene=` current. */
  onSceneChange?: (sceneId: string) => void;
  onContinueToMatching: () => void;
}) {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });

  const progress = useQuery({
    queryKey: ['review-progress', projectId],
    queryFn: () => api.reviewProgress(projectId),
  });

  const p = progress.data;
  const continueBlockedReason = continueBlockedReasonFor(p);
  const continueNote = continueNoteFor(p);

  // The rows the dialog names. Same keys as the review screen's own reads, so
  // they come out of the cache it already filled rather than a second fetch.
  const scriptVersionId = current.data?.scriptVersionId ?? null;
  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
  });
  const review = useQuery({
    queryKey: ['review-view', scriptVersionId],
    queryFn: () => api.reviewView(scriptVersionId ?? ''),
    enabled: scriptVersionId !== null,
  });

  const [asking, setAsking] = useState(false);

  const goOn = () => {
    // The route decides on the setup answer; make sure it is the current
    // one, or the decisions made here are not yet in it.
    void queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }).then(onContinueToMatching);
  };

  const continueAnyway = useMutation({
    mutationFn: () => api.markBreakdownContinued(projectId),
    onSuccess: (setup) => {
      queryClient.setQueryData(['project-setup', projectId], setup);
      setAsking(false);
      goOn();
    },
  });

  if (current.isPending) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="breakdown-step-loading">
        Loading the screenplay…
      </p>
    );
  }

  const script = current.data;

  // The route redirects to `nextStep` before this renders, so a production
  // with no screenplay does not normally reach here — but a URL is a URL.
  if (!script) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="breakdown-step-empty">
        There is no screenplay to review yet.
      </p>
    );
  }

  const undecided = (requirements.data ?? [])
    .filter((r) => r.status === 'PROPOSED' || r.status === 'DRAFT')
    .map((r) => ({ id: r.id, title: r.title }));
  const scenesWithoutRequirement = (review.data?.scenes ?? [])
    .filter((s) => s.requirements.length === 0)
    .map((s) => ({ id: s.id, sceneNumber: s.sceneNumber, slugline: s.slugline }));

  return (
    <>
      <ReviewRequirements
        projectId={projectId}
        scriptVersionId={script.scriptVersionId}
        projectTitle={projectTitle}
        scriptVersionLabel={`V${String(script.versionNumber)}`}
        // The Locations workflow: requirements and their decisions only. The
        // detailed breakdown is its own workflow, at `/elements`.
        focus="LOCATIONS"
        // The scene in the address opens on the right; the navigator keeps it
        // in view. (It used to scroll the rail to it and leave every scene
        // stacked below.)
        initialSceneId={focusSceneId}
        onSceneChange={onSceneChange}
        onContinueToMatching={() => {
          // A complete review goes straight on. Anything less asks first — on
          // the progress the button was drawn from, so the question and the
          // note beside the button never disagree.
          if (p === undefined) return;
          if (p.reviewComplete) {
            goOn();

            return;
          }

          continueAnyway.reset();
          setAsking(true);
        }}
        continueBlockedReason={continueBlockedReason}
        continueNote={continueNote}
      />

      {asking ? (
        <ContinueWithOpenBreakdownDialog
          undecided={undecided}
          scenesWithoutRequirement={scenesWithoutRequirement}
          approvedCount={p?.approved ?? 0}
          busy={continueAnyway.isPending}
          error={
            continueAnyway.error === null
              ? null
              : continueAnyway.error.message || 'Could not continue — try again in a moment.'
          }
          onStay={() => {
            setAsking(false);
          }}
          onContinue={() => {
            continueAnyway.mutate();
          }}
        />
      ) : null}
    </>
  );
}
